/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/ban-types */
import classnames from 'classnames';
import React, { useState } from 'react';
import { Button, Card, FormGroup } from 'react-bootstrap';
import { URLTextHTML } from '../../../form/URLText/URLText';
import HTMLEllipsis from 'react-lines-ellipsis/lib/html';
import checkIconFilled from 'v2//icons/checkIconFilled.svg';
import crossIconFilled from 'v2//icons/crossIconFilledRed.svg';
import questionMarkFilled from 'v2//icons/questionMarkFilled.svg';
import exclamationCircle from 'v2//icons/exclamationCircle.svg';
import DataFileBoxV2 from 'v2/DataFiles/DataFileBoxV2/DataFileBoxV2';
import { DataFileEntity } from 'v2/DataFiles/models';
import SearchResponseActions from './SearchResponseActions';
import styles from './searchResponseItem.module.scss';
import { SearchStatus } from './SearchResponses';
import {
  DataVendor,
  DjangoListResponse,
  ExternalLink,
  ProposalEntitySubmittedBy,
  RootState,
  SearchEntity,
} from 'v2/models';
import { useQuery } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import { useSelector } from 'react-redux';
import { PERMISSION_NAME, hasPermission } from 'v2/utils/permissions';
import ProviderInformationCard from './providerModals/ProviderInformationCard';
import { useTranslation } from 'react-i18next';
import { PostProcessKeys, TranslationKeys } from 'translations';
import { FreeUserBeacon } from 'v2/common/FreeUserBeacon/FreeUserBeacon';

export type RejectProposal = (
  rejectOption: number,
  rejectText?: string
) => void;

export interface SearchResponseItemProps {
  status: SearchStatus;
  proposalDetail: {
    proposalId: number;
    response?: string;
    dataFiles?: DataFileEntity[];
    answers?: Array<{
      question: string;
      response: string;
    }>;
    dataProvider: {
      id?: number;
      name: string;
      vague_description: string;
      short_description: string;
      long_description: string;
      logo: string;
      external_links?: ExternalLink[];
    };
    submittedBy?: ProposalEntitySubmittedBy;
    createdAt: string;
  };
  active: boolean;
  isThirdPartyResponse: boolean;
  hideIdentity: boolean;
}

type SearchResponseItemCompProps = SearchResponseItemProps & {
  className?: string;
  onAccepted: () => void;
  showActions?: boolean;
  showDataProviderInfo?: boolean;
  request: SearchEntity;
};

const SearchResponseItem = ({
  className = '',
  status,
  request,
  proposalDetail,
  active,
  isThirdPartyResponse,
  hideIdentity,
  onAccepted,
  showActions = true,
  showDataProviderInfo = true,
}: SearchResponseItemCompProps) => {
  const { t } = useTranslation();
  const permissions = useSelector(
    (state: RootState) => state.permissions.permissions
  );
  const [showMore, setShowMore] = useState(false);
  const [searchDescrTruncated, setSearchDescrTruncated] = useState(false);
  const icon = {
    accepted: checkIconFilled,
    rejected: crossIconFilled,
    pending: questionMarkFilled,
  };

  const submittedBy = isThirdPartyResponse
    ? ''
    : `${proposalDetail.submittedBy?.first_name || ''} ${
        proposalDetail.submittedBy?.last_name || ''
      }`;

  const providerName =
    proposalDetail.dataProvider.name ||
    t(TranslationKeys.dataProvider_one, {
      postProcess: PostProcessKeys.TitleCase,
    });

  const hasAttachedFiles = proposalDetail.dataFiles?.length;

  const { data, isSuccess } = useQuery(
    ['DataVendor_fetchingDataVendor', proposalDetail.dataProvider.id],
    async () => {
      const response = await restFactory.get<DjangoListResponse<DataVendor>>(
        `/dj/api/v1/data_vendor/`,
        {
          page: 1,
          data_provider: proposalDetail.dataProvider?.id,
        }
      );
      if (!response.results.length) {
        return undefined;
      }
      return response.results[0];
    },
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled:
        hasPermission(permissions, [PERMISSION_NAME.DRM]) &&
        showDataProviderInfo,
    }
  );

  const showExpandButton =
    proposalDetail.answers?.length ||
    proposalDetail.dataFiles?.length ||
    searchDescrTruncated;

  const renderNewResponseActions = () => {
    return status === 'new' && active ? (
      <SearchResponseActions
        key={proposalDetail.proposalId}
        proposalId={proposalDetail.proposalId}
        isThirdPartyResponse={isThirdPartyResponse}
        onAccepted={onAccepted}
        providerName={providerName}
      />
    ) : null;
  };

  const handleTruncateSearchDescr = ({ clamped }: { clamped: boolean }) => {
    if (clamped && clamped !== searchDescrTruncated) {
      setSearchDescrTruncated(clamped);
    }
  };

  return (
    <div
      className={classnames(
        'position-relative rounded border-light5',
        styles.root,
        className
      )}
    >
      <FreeUserBeacon />
      <Card
        className={classnames(
          'rounded-left border-light5',
          styles.cardContainer
        )}
      >
        {status && status !== 'new' ? (
          <Card.Header
            className={classnames('rounded-0', {
              'text-danger-dark bg-danger-light': status === 'rejected',
              'text-primary bg-primary-light': status === 'accepted',
              'text-dark bg-light-gray': status === 'pending',
            })}
          >
            <img src={icon[status]} className="mr-2" alt="searchStatusIcon" />
            <span className={classnames('text-uppercase', styles.cardTitle)}>
              {status}
            </span>
          </Card.Header>
        ) : null}

        <Card.Body className={classnames('p-0 rounded')}>
          <div className="p-4 h-100 d-flex flex-column justify-content-between">
            <div>
              <FormGroup className={styles.responseField}>
                <label className="text-primary-dark">Response:</label>
                <HTMLEllipsis
                  onReflow={handleTruncateSearchDescr}
                  unsafeHTML={URLTextHTML(proposalDetail.response || '')}
                  maxLine={!showMore ? 3 : Infinity}
                  ellipsis="..."
                  basedOn="letters"
                />
              </FormGroup>
              {isThirdPartyResponse ? (
                <div
                  className={classnames(
                    'bg-light py-3 px-5 position-relative mb-3 mt-4',
                    styles.thirdPartyResponseContainer
                  )}
                >
                  <p className="text-primary-dark font-weight-bold position-relative mb-1">
                    <img
                      className={classnames(
                        'position-absolute',
                        styles.exclamationIcon
                      )}
                      src={exclamationCircle}
                      alt="exclamation"
                    ></img>
                    This response was submitted by Nomad, not by the data
                    provider.
                  </p>
                  <p className="text-primary-dark mb-1">
                    This typically happens when the provider only sells online.
                    Click on their profile for their website.
                  </p>
                </div>
              ) : null}

              <Button
                className={classnames('p-0', { invisible: !showExpandButton })}
                variant="link"
                onClick={() => setShowMore(!showMore)}
                disabled={!showExpandButton}
              >
                {showMore ? 'Show Less' : 'Show More'}
              </Button>
              {showMore ? (
                <div className="pt-3">
                  {proposalDetail.answers?.map((item, index) => (
                    <FormGroup key={index} className="mt-4">
                      <label className="text-primary-dark">
                        {item.question}
                      </label>
                      <p>{item.response}</p>
                    </FormGroup>
                  ))}
                  {hasAttachedFiles ? (
                    <>
                      <FormGroup className="mt-4">
                        <label className="text-primary-dark">
                          Attached Files
                        </label>
                      </FormGroup>
                      {proposalDetail.dataFiles?.map((dataFile) => (
                        <DataFileBoxV2
                          key={dataFile.id}
                          className="mb-3"
                          data={dataFile}
                          allowDownload={true}
                        />
                      ))}
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
            {showActions && renderNewResponseActions()}
          </div>
        </Card.Body>
      </Card>
      {showDataProviderInfo && (
        <ProviderInformationCard
          submittedBy={proposalDetail.submittedBy}
          submittedByTitle={submittedBy}
          dataProvider={proposalDetail.dataProvider}
          hideIdentity={hideIdentity}
          dataVendorQuery={{ data, isSuccess }}
          request={request}
        />
      )}
    </div>
  );
};

export default SearchResponseItem;
